import * as L from 'leaflet';
import { trackWmsReadiness } from './wms-readiness';

/** WMS with authorization and completion only after a successful image load. */
export class AuthenticatedWmsLayer extends L.TileLayer.WMS {
  private readonly cancellations = new Map<HTMLElement, () => void>();

  constructor(url: string, options: L.WMSOptions, private readonly headers: Record<string, string>) {
    super(url, options);
    trackWmsReadiness(this);
    this.on('tileunload', (event: L.TileEvent) => this.cancellations.get(event.tile)?.());
    this.on('remove', () => {
      Array.from(this.cancellations.values()).forEach((cancel) => cancel());
    });
  }

  override createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const img = document.createElement('img');
    img.alt = '';
    img.setAttribute('role', 'presentation');
    const controller = new AbortController();
    let objectUrl: string | undefined;
    let settled = false;
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
      }
      this.cancellations.delete(img);
    };
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      // Keep a loaded tile's blob URL available while it remains on the map.
      // Report capture clones the image and needs to read that URL again.
      if (error) {
        cleanup();
      } else {
        img.onload = null;
        img.onerror = null;
      }
      done(error, img);
    };
    this.cancellations.set(img, () => {
      settled = true;
      controller.abort();
      cleanup();
    });
    img.onload = () => finish();
    img.onerror = () => finish(new Error('WMS tile image could not be decoded'));
    fetch(this.getTileUrl(coords), { headers: this.headers, signal: controller.signal, mode: 'cors' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`WMS tile request failed: HTTP ${response.status}`);
        const blob = await response.blob();
        if (settled) return;
        objectUrl = URL.createObjectURL(blob);
        img.src = objectUrl;
      })
      .catch((error) => finish(error instanceof Error ? error : new Error(String(error))));
    return img;
  }
}
